import React, { useMemo } from "react";
import { Typography, Container, Grid, Card, CardContent, CardMedia, Box } from "@mui/material";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import AppAppBar from "../../components/AppAppBar";
import Footer from "../../components/Footer";

export const fileNameToSlug = (fileName) => {
    return fileName
        .replace(".js", "")
        .replace(/([A-Z])([A-Z0-9])/g, "$1-$2")
        .replace(/([a-z])([A-Z0-9])/g, "$1-$2")
        .replace(/([0-9]+)/g, "-$1-")
        .toLowerCase()
        .replace(/^-+|-+$/g, "")
        .replace(/-+/g, "-");
};

export const getAllFeatures = () => {
    const pages = [];
    const context = require.context("./", false, /\.js$/);

    context.keys().forEach((path) => {
        if (path === "./index.js") return;

        try {
            const module = context(path);
            const Component = module.default;
            if (Component && typeof Component === "function") {
                const data = Component().props;
                if (data) {
                    const slug = fileNameToSlug(path.replace("./", ""));
                    pages.push({
                        title: data.title,
                        subtitle: data.subtitle,
                        description: data.meta?.description,
                        image: data.heroImage,
                        url: `/features/${slug}`,
                        date: data.date
                    });
                }
            }
        } catch (err) {
            console.warn(`Could not load feature data from ${path}:`, err);
        }
    });

    return pages.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const FeatureIndex = () => {
    const features = useMemo(() => getAllFeatures(), []);
    const baseUrl = "https://www.getmarter.com";

    return (
        <>
            <Helmet>
                <title>Walmart Seller Tool Features | Marter</title>
                <meta
                    name="description"
                    content="Every Marter feature for Walmart sellers: product research, price history tracking, profit and fee calculation, sales estimates, and the Chrome extension. One plan, $24/month."
                />
                <link rel="canonical" href={`${baseUrl}/features`} />
                <meta property="og:title" content="Walmart Seller Tool Features | Marter" />
                <meta property="og:url" content={`${baseUrl}/features`} />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
                            { "@type": "ListItem", position: 2, name: "Features", item: `${baseUrl}/features` }
                        ]
                    })}
                </script>
            </Helmet>

            <AppAppBar />
            <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff", pt: { xs: "84px", md: "92px" } }}>
                <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
                    <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, maxWidth: "800px", mx: "auto" }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 800,
                                color: "#1a1a1a",
                                fontSize: { xs: "2rem", md: "3rem" },
                                mb: 3
                            }}
                        >
                            Marter Features
                        </Typography>
                        <Typography
                            variant="h2"
                            sx={{
                                color: "text.secondary",
                                fontSize: { xs: "1.125rem", md: "1.25rem" },
                                lineHeight: 1.6
                            }}
                        >
                            Everything Walmart Seller Center doesn't show you — profit, fees, sales estimates,
                            price history, and seller data, on the product page itself. One plan, $24/month.
                        </Typography>
                    </Box>

                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: 6 }}>
                        {features.map((f, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Link
                                    to={f.url}
                                    style={{ textDecoration: "none" }}
                                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                >
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            transition: "all 0.3s ease",
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            border: "1px solid #eaeaea",
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                boxShadow: "0 12px 24px rgba(0,0,0,0.06)"
                                            }
                                        }}
                                    >
                                        {f.image && (
                                            <CardMedia
                                                component="img"
                                                height="240"
                                                image={f.image}
                                                alt={f.title}
                                                sx={{ objectFit: "cover" }}
                                            />
                                        )}
                                        <CardContent sx={{ p: 3, flexGrow: 1 }}>
                                            <Typography
                                                variant="h3"
                                                component="h2"
                                                sx={{
                                                    color: "#1a1a1a",
                                                    fontWeight: 700,
                                                    fontSize: "1.25rem",
                                                    lineHeight: 1.4,
                                                    mb: 2
                                                }}
                                            >
                                                {f.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "0.9375rem",
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                {f.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
                <Footer />
            </Box>
        </>
    );
};

export default FeatureIndex;
